/**
 * Language Selector Component
 * 
 * Dropdown for selecting UI language
 */

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import useTranslation from '../hooks/useTranslation';

const LanguageSelector = () => {
  const { currentLanguage, changeLanguage, supportedLanguages, isTranslating } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Get current language info
  const currentLang = supportedLanguages.find(lang => lang.code === currentLanguage) || supportedLanguages[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = async (langCode) => {
    setIsOpen(false);
    if (langCode !== currentLanguage) {
      await changeLanguage(langCode);
    }
  };

  return (
    <LanguageSelectorWrapper ref={dropdownRef}>
      <LanguageButton 
        onClick={() => setIsOpen(!isOpen)}
        disabled={isTranslating}
      >
        <FlagIcon>{currentLang.flag}</FlagIcon>
        <LanguageCode>{currentLang.code.toUpperCase()}</LanguageCode>
        <Arrow isOpen={isOpen}>▼</Arrow>
      </LanguageButton>

      {isOpen && (
        <DropdownMenu>
          {supportedLanguages.map((lang) => (
            <LanguageOption
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              isActive={lang.code === currentLanguage}
            >
              <FlagIcon>{lang.flag}</FlagIcon>
              <LanguageName>{lang.name}</LanguageName>
              {lang.code === currentLanguage && <CheckMark>✓</CheckMark>}
            </LanguageOption>
          ))}
        </DropdownMenu>
      )}

      {isTranslating && (
        <LoadingIndicator>
          <Spinner />
        </LoadingIndicator>
      )}
    </LanguageSelectorWrapper>
  );
};

export default LanguageSelector;

// Styled Components
const LanguageSelectorWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: ${({ theme }) => theme.color.secondaryBg || '#f0f0f0'};
  border: 1px solid ${({ theme }) => theme.color.border || '#ddd'};
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.darkerText || '#333'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.color.background || '#e0e0e0'};
    border-color: ${({ theme }) => theme.color.primary || '#4CAF50'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.color.primary || '#4CAF50'}33;
  }
`;

const FlagIcon = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const LanguageCode = styled.span`
  font-weight: 600;
  text-transform: uppercase;
`;

const Arrow = styled.span`
  font-size: 10px;
  transition: transform 0.2s ease;
  transform: ${({ isOpen }) => isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 200px;
  background-color: ${({ theme }) => theme.color.secondaryBg || '#fff'};
  border: 1px solid ${({ theme }) => theme.color.border || '#ddd'};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1000;
  animation: slideDown 0.2s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LanguageOption = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background-color: ${({ isActive, theme }) => 
    isActive ? (theme.color.primaryBg || '#e8f5e9') : 'transparent'};
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: ${({ theme }) => theme.color.darkerText || '#333'};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.color.background || '#f5f5f5'};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.color.border || '#eee'};
  }
`;

const LanguageName = styled.span`
  flex: 1;
  text-align: left;
  font-weight: 500;
`;

const CheckMark = styled.span`
  color: ${({ theme }) => theme.color.primary || '#4CAF50'};
  font-weight: bold;
  font-size: 16px;
`;

const LoadingIndicator = styled.div`
  position: absolute;
  top: 50%;
  right: -30px;
  transform: translateY(-50%);
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${({ theme }) => theme.color.border || '#ddd'};
  border-top-color: ${({ theme }) => theme.color.primary || '#4CAF50'};
  border-radius: 50%;
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

